import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const NavLink = ({ link, isActive, onHover, onClick }) => {
    const ref = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);

    const handleMouse = (e) => {
        const { clientX, clientY } = e;
        const { left, top, width, height } = ref.current.getBoundingClientRect();
        const x = clientX - (left + width / 2);
        const y = clientY - (top + height / 2);
        setPosition({ x: x * 0.3, y: y * 0.3 });
    };

    const reset = () => {
        setPosition({ x: 0, y: 0 });
    };

    return (
        <motion.a
            ref={ref}
            href={link.href}
            onMouseMove={handleMouse}
            onMouseLeave={() => {
                reset();
                onHover(null);
                setIsHovered(false);
            }}
            onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const parentRect = e.currentTarget.offsetParent.getBoundingClientRect();

                onHover({
                    left: rect.left - parentRect.left,
                    width: rect.width,
                    height: rect.height,
                });

                setIsHovered(true);
            }}
            onClick={onClick}
            animate={{ x: position.x, y: position.y }}
            transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
            className={`relative z-10 px-5 py-2 text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                isActive
                    ? 'text-brand-teal'
                    : 'text-dark-text/70 hover:text-dark-text'
            } ${isHovered ? '!text-white' : ''}`}
        >
            {link.name}
        </motion.a>
    );
};

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('Home');
    const [hoveredRect, setHoveredRect] = useState(null);
    const [scrollProgress, setScrollProgress] = useState(0);

    const navLinks = useMemo(() => [
        { name: 'Home', id: 'home', href: '#home' },
        { name: 'About', id: 'about', href: '#about' },
        { name: 'Service', id: 'service', href: '#service' },
        { name: 'Portfolio', id: 'portfolio', href: '#portfolio' },
        { name: 'Careers', id: 'careers', href: '#careers' },
        { name: 'Contact', id: 'contact', href: '#contact' },
    ], []);

    useEffect(() => {
        const handleScroll = () => {
            const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
            const currentScroll = window.scrollY;
            setScrollProgress((currentScroll / totalScroll) * 100);

            setIsScrolled(currentScroll > 50);

            const sections = navLinks.map(link => document.getElementById(link.id));
            const scrollPos = currentScroll + 200;

            sections.forEach((section, index) => {
                if (section) {
                    const top = section.offsetTop;
                    const height = section.offsetHeight;
                    if (scrollPos >= top && scrollPos < top + height) {
                        setActiveTab(navLinks[index].name);
                    }
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [navLinks]);

    return (
        <header className="fixed top-0 left-0 right-0 z-[100] flex flex-col items-center py-6 md:py-8 transition-all duration-500">
            
            {/* Scroll Progress */}
            <div className="absolute top-0 left-0 w-full h-[3px] bg-slate-200 overflow-hidden">
                <motion.div
                    className="h-full bg-brand-teal"
                    animate={{ width: `${scrollProgress}%` }}
                />
            </div>

            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{
                    y: 0,
                    opacity: 1,
                    width: isScrolled ? 'fit-content' : '95%',
                    padding: isScrolled ? '6px' : '12px 24px',
                }}
                className="relative flex items-center justify-between glass-effect rounded-[100px] border border-slate-200 shadow-xl transition-all duration-500 overflow-visible"
            >

                {/* Logo */}
                <motion.div
                    animate={{
                        opacity: isScrolled ? 0 : 1,
                        width: isScrolled ? 0 : 'auto',
                    }}
                    className={`flex items-center gap-3 overflow-hidden ${isScrolled ? 'pointer-events-none' : ''}`}
                >
                    <img src="/images/Codebytz.png" alt="Codebytz Logo" className="w-12 h-12 object-contain"/>
                    <span className="text-xl font-black">
                        CODE<span className="text-brand-teal">BYTZ</span>
                    </span>
                </motion.div>

                {/* Desktop Nav */}
                <div className="hidden lg:flex items-center gap-1 relative bg-slate-100 p-1 rounded-full border border-slate-200">

                    {/* FIXED LIQUID INDICATOR */}
                    {hoveredRect && (
                        <motion.div
                            layoutId="nav-liquid"
                            initial={false}
                            animate={{
                                left: hoveredRect.left,
                                width: hoveredRect.width,
                                height: hoveredRect.height,
                                top: 0,
                                opacity: 1
                            }}
                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                            className="absolute bg-brand-teal rounded-full"
                        />
                    )}

                    {navLinks.map((link) => (
                        <NavLink
                            key={link.name}
                            link={link}
                            isActive={activeTab === link.name}
                            onHover={setHoveredRect}
                            onClick={() => {
                                setActiveTab(link.name);
                                document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' });
                            }}
                        />
                    ))}
                </div>

                {/* Contact Button (Desktop) */}
                <motion.div
                    animate={{
                        opacity: isScrolled ? 0 : 1,
                        width: isScrolled ? 0 : 'auto',
                        x: isScrolled ? 20 : 0
                    }}
                    className={`hidden md:block overflow-hidden ${isScrolled ? 'pointer-events-none' : ''}`}
                >
                    <button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="bg-brand-teal text-white px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all border border-brand-teal/30 hover:shadow-lg hover:shadow-brand-teal/20 active:scale-95 whitespace-nowrap"
                    >
                        Grow With Us
                    </button>
                </motion.div>

                {/* Mobile Button */}
                <div className="lg:hidden flex items-center pr-2">
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-100"
                    >
                        {isMobileMenuOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </motion.nav>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                        animate={{ opacity: 1, backdropFilter: "blur(16px)" }}
                        exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                        className="fixed inset-0 bg-white/90 z-[200] flex flex-col items-center justify-center overflow-hidden"
                    >
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="absolute top-8 right-8 w-12 h-12 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 transition-colors shadow-sm"
                        >
                            <X size={24} />
                        </button>

                        <div className="flex flex-col items-center gap-6 w-full px-6">
                            {navLinks.map((link, i) => (
                                <motion.a
                                    key={link.name}
                                    href={link.href}
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: 30, opacity: 0 }}
                                    transition={{ delay: i * 0.1, type: "spring", stiffness: 200, damping: 20 }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsMobileMenuOpen(false);
                                        setActiveTab(link.name);
                                        document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    className={`text-3xl font-black uppercase tracking-widest transition-colors ${
                                        activeTab === link.name ? 'text-brand-teal' : 'text-slate-800 hover:text-brand-teal'
                                    }`}
                                >
                                    {link.name}
                                </motion.a>
                            ))}
                            
                            <motion.button
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 30, opacity: 0 }}
                                transition={{ delay: navLinks.length * 0.1, type: "spring", stiffness: 200, damping: 20 }}
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="mt-8 bg-brand-teal text-white px-10 py-4 rounded-full text-sm font-black uppercase tracking-[0.2em] shadow-xl shadow-brand-teal/30 active:scale-95"
                            >
                                Grow With Us
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;